/*
 * App Entry Point.
 */
import { MutationCache, Query, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import { createRoot } from 'react-dom/client';

import { FetcherError } from 'utils';
import { Toast } from 'widgets';

import { App } from './App';
import { addRumError, initializeRemoteLogging } from './datadog-logging';
import './styles/tailwind.css';
import { logout } from './views/auth/auth-queries';

type AnyQuery = Query<unknown, unknown, unknown, readonly unknown[]>;

const handleError = (err: Error, query?: AnyQuery) => {
    const error = err as FetcherError;

    // Handle unauthorized - logout user
    if (error.status === 401) {
        const skipHandling = query?.meta?.skipUnauthorizedHandling;
        if (!skipHandling) {
            logout();
        }
        return;
    }

    // Log to DataDog
    addRumError(error, {
        errorId: error.errorId,
        errorType: error.errorType,
        backendMessage: error.backendMessage,
        status: error.status,
        url: error.url,
    });
};

initializeRemoteLogging();

const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: (error, query) => handleError(error, query),
    }),
    mutationCache: new MutationCache({
        onError: (err: Error) => {
            handleError(err);
            const error = err as FetcherError;
            if (!error.hideError) {
                Toast.forError(error);
            }
        },
    }),
    defaultOptions: {
        queries: {
            retry: (failureCount, err: unknown) => {
                const error = err as FetcherError;
                return error.errorType === 'NETWORK_ERROR' && failureCount <= 2;
            },
            throwOnError: (err: unknown) => {
                const error = err as FetcherError;
                // only throw error towards the boundary if it was not a network error, otherwise silently fall back to an internal error state
                return error.errorType === 'NETWORK_ERROR';
            },
        },
        mutations: {
            retry: false,
        },
    },
});
const container = document.getElementById('root');
const root = createRoot(container!);

if (__DEV__) {
    root.render(
        <QueryClientProvider client={queryClient}>
            <App />
            <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
        </QueryClientProvider>,
    );
} else {
    root.render(
        <QueryClientProvider client={queryClient}>
            <App />
        </QueryClientProvider>,
    );
}
